import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { List, ListRowProps, ListRowRenderer, WindowScroller } from "react-virtualized";
import { useDocumentScrollContext } from "@newHooks/context/document/documentScroll.context";
import { DocsPdfContextProvider } from "@newHooks/context/viewer/docsPdf.context";
import { useFileDetailViewerContext } from "@newHooks/context/viewer/fileDetailViewer.context";
import { baseHeight } from "@assets/theme/baseSize/baseHeight";
import { FileDetailViewerRenderer } from "./FileDetailViewerRenderer";
import { DocumentSignerSignatureExt, PdfPageScrollType, PdfPageSpecType, PdfRelativeSizeReqType, PdfRelativeSizeSpec } from "./types";
import { DocumentSignerAttrType, DocumentSignResource, DocumentSignResourceReferrerExt } from "@maven-rest/docs/DocumentSign.dto";
import { DocumentAccessRequesterType } from "@maven-rest/docs/DocumentSigner.dto";
import { DocsPdf, DocsPdfPageSizeinfo } from "@maven-rest/docs/pdf/DocsPdf.dto";

export type FileDetailViewerProps = {
  fileId: bigint;
  docsPdf: Pick<DocsPdf, "id" | "metainfo">;
  documentSign?: DocumentSignResource | DocumentSignResourceReferrerExt;
  token?: string;
  accessToken?: string;
  onMarkerClicked?: (signerUuid: string, attrUuid: string, attr: DocumentSignerAttrType) => void;
  signatures?: DocumentSignerSignatureExt[];
  baseZoom?: number;
  documentAccessRequesterType?: DocumentAccessRequesterType;
  latestDocumentId?: bigint;
};

export function FileDetailViewer(props: FileDetailViewerProps) {
  const { docsPdf, baseZoom = 1.0 } = props;
  const { appHeaderAndDetailHeight, appDocsPdfPageSpacingHeight } = baseHeight;
  const { metainfo } = docsPdf;

  const { zoom, setPageInfo } = useFileDetailViewerContext();
  const { addPageScrollDelegate, removePageScrollDelegate } = useDocumentScrollContext();

  const [scroll, setScroll] = useState<PdfPageScrollType>({ scrollLeft: 0, scrollTop: 0 });
  const listRef = useRef<List>(null);
  const scrollerRef = useRef<WindowScroller>(null);

  const { calcPages, accPagesHeight, pagesMaxWidth } = useMemo(
    () => makePdfMetainfoRelativeSize({ pageSpacingHeight: appDocsPdfPageSpacingHeight, metainfo, zoom, baseZoom }),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metainfo, zoom, baseZoom]
  );

  const { listStyle, listContainerStyle }: { listStyle: CSSProperties; listContainerStyle: CSSProperties } = useMemo(() => {
    return {
      listStyle: {
        height: accPagesHeight,
        maxWidth: pagesMaxWidth,
        overflow: "visible",
        userSelect: "none",
      },
      listContainerStyle: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: accPagesHeight,
        maxWidth: pagesMaxWidth,
        overflow: "visible",
        userSelect: "none",
      },
    };
  }, [accPagesHeight, pagesMaxWidth]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getRowHeight = useCallback((index: number) => calcPages[index].height + appDocsPdfPageSpacingHeight, [calcPages]);

  useEffect(() => {
    const pageScrollDelegate = (page: number) => {
      const scrollPage = calcPages.find((e) => e.page === page);

      window.scrollTo({ top: scrollPage!.top + appHeaderAndDetailHeight, behavior: "smooth" });
    };

    addPageScrollDelegate(pageScrollDelegate);
    listRef.current?.Grid?.recomputeGridSize();

    return () => {
      removePageScrollDelegate(pageScrollDelegate);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom]);

  useEffect(() => {
    const viewportBottom = Math.round(window.innerHeight + window.scrollY);

    if (viewportBottom >= document.body.scrollHeight) {
      setPageInfo({ current: calcPages.length, total: calcPages.length });
      return;
    }

    const pageBoundaryHeight = scroll.scrollTop + appHeaderAndDetailHeight - appDocsPdfPageSpacingHeight;
    const currentPage = calcPages.find(({ top, height }) => top + height >= pageBoundaryHeight);

    setPageInfo({ current: currentPage?.page || 1, total: calcPages.length });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accPagesHeight, calcPages, scroll]);

  const pdfPageRenderer: ListRowRenderer = ({ index, key }: ListRowProps) => {
    const { baseZoom, ...rest } = props;

    return <FileDetailViewerRenderer key={key} calcPage={calcPages[index]} zoom={zoom} {...rest} />;
  };

  return (
    <DocsPdfContextProvider docsPdf={docsPdf}>
      <WindowScroller ref={scrollerRef} height={accPagesHeight} {...scroll} onScroll={setScroll}>
        {({ width, height, registerChild, scrollTop }) => (
          <div ref={registerChild as React.LegacyRef<HTMLDivElement>}>
            <List
              ref={listRef}
              autoHeight
              height={height} // 리스트의 높이
              rowCount={calcPages.length} // 아이템의 총 개수
              style={listStyle}
              containerStyle={listContainerStyle}
              rowHeight={(e) => getRowHeight(e.index)} // 각 항목의 높이
              rowRenderer={pdfPageRenderer}
              scrollTop={scrollTop} // 스크롤 위치
              width={width} // 리스트의 너비
            />
          </div>
        )}
      </WindowScroller>
    </DocsPdfContextProvider>
  );
}

export function makePdfMetainfoRelativeSize(req: PdfRelativeSizeReqType): {
  calcPages: Array<PdfPageSpecType>;
  accPagesHeight: number;
  pagesMaxWidth: number;
} {
  const { metainfo, zoom, baseZoom, pageSpacingHeight } = req;

  return metainfo.pages.reduce(
    (acc, { page, size }) => {
      const { width, height, baseWidth, baseHeight } = getRelativeSize({ sizeInfo: size, zoom, baseZoom });

      acc.calcPages.push({ page, width, height, top: acc.accPagesHeight, baseWidth, baseHeight });
      acc.accPagesHeight += height + pageSpacingHeight;
      acc.pagesMaxWidth = Math.max(acc.pagesMaxWidth, width);
      return acc;
    },
    { calcPages: [] as Array<PdfPageSpecType>, accPagesHeight: 0, pagesMaxWidth: 0 }
  );
}

function getRelativeSize(
  req: Omit<PdfRelativeSizeReqType, "metainfo" | "pageSpacingHeight"> & { sizeInfo: DocsPdfPageSizeinfo }
): PdfRelativeSizeSpec {
  const { sizeInfo, zoom, baseZoom } = req;
  const calcZoom = typeof zoom === "number" ? zoom : 1.0;
  const width = sizeInfo[0] * baseZoom;
  const height = sizeInfo[1] * baseZoom;

  return { width: width * calcZoom, height: height * calcZoom, baseWidth: width, baseHeight: height };
}
